// Defined in general.js
/* global dcAnalytics */

import { getIndex, getPage, TRACKING_PARAMS } from './requestUtils.js';
import { decodeXml } from '../utils/encodeXML.ts';
import { getURLParam, getURLParams } from '../adapters/dupes/queryParams.js';
import { locationParamNames } from './locationUtils.js';

// These URLs need to match the current page's URL, so we start them with a /
const TEACHER_SEARCH_URL = '/common/teacherSuggest.html?excludeTestRecords=true&fieldsToSearch=FULL_NAME_AND_LOCATION&APIKey=' + dcAPIKey;
const SCHOOL_SEARCH_URL = '/teachers/schoolSuggest.html?excludeTestRecords=true&fieldsToSearch=FULL_NAME_AND_LOCATION&fieldsToRetrieve=LOCATION_ONLY&APIKey=' + dcAPIKey;
const SCHOOL_MAP_SEARCH_URL = '/teachers/schoolSuggest.html?excludeTestRecords=true&fieldsToSearch=FULL_NAME_AND_LOCATION&fieldsToRetrieve=FULL_SCHOOL_CARD&geoSort=true&APIKey=' + dcAPIKey;
const ESSENTIALS_LIST_SEARCH_URL = '/common/essentials-list.json';

const ESSENTIALS_CONSTANTS = {
    // Use this if we're intentionally overfetching.
    MAX_WHEN_SHUFFLING: 20,
    // Use this if we're fetching from project-search and just want two matching results to embed in the project-results
    EMBEDDED_TO_DISPLAY: 2,
    // Use this if we're fetching from project-search and want to promote an unrelated essentials list
    PROMOS_TO_DISPLAY: 1,
};

export const ESSENTIALS_LIST_PARAM_NAMES = TRACKING_PARAMS
    .concat(locationParamNames)
    .concat([
        'equityFocus',
        'moderateHighEconomicNeed', 'highLevelPoverty', 'highestLevelPoverty',
        'metroType', 'schoolType', 'teacherType',
        'teacherBirthday',
        'essentialsListsOnly',
        'page',
    ]);

export const unescapeBreadcrumb = function (breadcrumb) {
    breadcrumb && breadcrumb.forEach(function (crumb) {
        crumb[1] = decodeXml(crumb[1]);
        crumb[2] = decodeXml(crumb[2]);
    });
};

export const processFacetCounts = function (counts) {
    const subjects = {};
    if (counts.subject != null) {
        for (const key in counts.subject) {
            if (counts.subject.hasOwnProperty(key)) {
                if (typeof counts.subject[key] === 'number') {
                    subjects[key] = counts.subject[key];
                } else if (typeof counts.subject[key] === 'string' && counts.subject[key].trim() != '') {
                    const fociiCounts = counts.subject[key].replace('{', '').replace('}', '').split(',');
                    for (const key2 in fociiCounts) {
                        if (fociiCounts.hasOwnProperty(key2)) {
                            const fArray = fociiCounts[key2].split('=');
                            if (fArray != null && fArray.length > 1 && fArray[1] != '') {
                                subjects[fArray[0].trim()] = fArray[1];
                            }
                        }
                    }
                }
            }
        }
    }
    counts.subject = subjects;
    return counts;
};

export const searchTimeout = function () {
    // Local search loads can be slow
    const isLocal = baseURL.indexOf('local') > -1;
    return isLocal ? 60000 : 10000;
};

/**
 * @param {string} searchURL The url to convert
 * @param {number} perPage Results per page
 * @param {?number} max The max results to load
 * @return {?string} A json_feed url
 */
export const convertToApiURL = function (searchURL, perPage, max) {
    if (
        schoolSearchOnly(searchURL) ||
        teacherSearchOnly(searchURL) ||
        essentialsListSearchOnly(searchURL)
    ) {
        return null;
    }

    const index = getIndex(searchURL, perPage);
    const query = new Uri(searchURL).deleteQueryParam('page').replace('index', index)
        .query().substring(1);

    let jsonURL;
    // Landing page uses a different url that is permitted by crawlers
    if (getURLParam(searchURL, 'landing') === 'true') {
        jsonURL = baseURL + 'common/json_feed_landing_page.html?showFacetCounts=true&APIKey=' + dcAPIKey;
    } else {
        jsonURL = baseURL + 'common/json_feed.html?showFacetCounts=true&APIKey=' + dcAPIKey;
    }

    // Temporary code for A/B tests
    // We leave it around since we periodically want to A/B test urgency
    //        if (typeof readCookie === "function") {
    //             var altUrgency = parseInt(readCookie("alternativeUrgency"), 10);
    //             if (altUrgency) {
    //                 jsonURL += "&alternativeUrgency=" + altUrgency;
    //             }
    //
    //             // This can be uncommented after the experiment to clear out the cookies
    //            eraseCookie("alternativeUrgency");
    //        }

    if (max) {
        jsonURL += '&max=' + max;
    }

    if (query) {
        jsonURL += '&' + query;
    }

    return jsonURL;
};

/**
 * @param {string} searchURL The url to convert
 * @param {?number} max The max results to load
 * @return {?string} A json_feed url
 */
export const convertToEssentialsListMapSearchApiURL = function (searchURL, max) {
    const index = 0;
    const query = new Uri(searchURL).deleteQueryParam('page').replace('index', index)
        .query().substring(1);

    let jsonURL = ESSENTIALS_LIST_SEARCH_URL + '?APIKey=' + dcAPIKey;

    if (max) {
        jsonURL += '&max=' + max;
    }

    if (query) {
        jsonURL += '&' + query;
    }

    return jsonURL;
};

export const convertToSchoolMapSearchUrl = function(searchURL, max) {
    return buildSchoolOrTeacherURL(searchURL, SCHOOL_MAP_SEARCH_URL, true, max);
}

/**
     * Updates a search URL with new parameters
     * @param {object} params The parameters to add/remove to/from the URL
     * @param {string} currentURL The current URL
     * @param {object[]=} filters The search filters, which can be used to determine parent/child relationships
     * @return {string} The update URL
     */
export const makeSearchURL = function (params, currentURL, filters) {
    const uri = new Uri(currentURL);

    // We allow these params to have multiple values
    const multiValuedParams = {
        proposalType: true,
        proposalTypeCombo: true,
        gradeType: true,
        gradeLevel: true,
        // subject is a special case of multiple values
    };

    let children, isChildSelected;
    if (filters) {
        const filter = getFilterByParamValues(params, filters);
        children = filter && filter.children;
        isChildSelected = isChildFilterSelected(children, uri);
    }

    if (!params.page || params.page === 1) {
        // Reset page to 1 if we're applying new filters
        // We remove page=1 from the URL since it's the default
        params.page = null;
    }

    uri.delete('index');
    if (uri.hasQueryParam('landing')) {
        uri.delete('landing', 'true');
    }

    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const value = params[key];

            if (key.indexOf('subject') === 0) {
                // each subject param can be a parent or child subject, so it requires special handling
                const parentValue = '-' + key.slice(7);
                if (value == parentValue) {
                    if (params.remove === true) {
                        uri.delete(key);
                    } else {
                        uri.replace(key, value);
                    }
                } else {
                    if (params.remove === true) {
                        uri.delete(key, value);
                        if (!uri.hasQueryParam(key)) {
                            // If we've removed all children, re-add the parent
                            uri.add(key, parentValue);
                        }
                    } else {
                        // Remove the parent before adding a child
                        uri.delete(key, parentValue)
                            .add(key, value);
                    }
                }
            } else if (multiValuedParams[key] === true) {
                if (params.remove === true) {
                    if (isChildSelected) {
                        // Clicking on a parent when its children are selected should
                        // remove the child values instead of removing the parent
                        children.forEach(child =>
                            uri.delete(child.paramName, child.value),
                        );
                    } else {
                        uri.delete(key, value);
                    }
                } else {
                    uri.add(key, value);
                }
            } else {
                if (value == null || value == 0 || params.remove === true) {
                    uri.delete(key);
                    if (key === 'matchingId' && value != '-1') {
                        // Re-add matching parent ID
                        uri.add(key, '-1');
                    } else if (key === 'historical') {
                        // Remove child
                        uri.delete('hasProposalPhotos');
                    }
                } else {
                    uri.replace(key, value);
                }
            }
        }
    }

    return uri.toString();
};

/**
     * @param {object[]=} children The children of a filter
     * @param {Uri} uri
     * @return {boolean}
     */
export const isChildFilterSelected = function (children, uri) {
    return !!children && children.some(child =>
        uri.has(child.paramName, child.value),
    );
};

/**
     * @param {object} params The parameters to add to/remove from the URL
     * @param {object[]} filters The search filters, which can be used to determine parent/child relationships
     * @return {?object} The filter, if applicable
     */
function getFilterByParamValues(params, filters) {
    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            for (let i = 0; i < filters.length; i++) {
                const filterCategory = filters[i];
                const filtersInCategory = filterCategory[Object.keys(filterCategory)[0]];

                for (let j = 0; j < filtersInCategory.length; j++) {
                    const filter = filtersInCategory[j];
                    if (filter.paramName == key && filter.value == params[key]) {
                        return filter;
                    }
                }
            }
        }
    }
}

export const makeSchoolURL = function (baseSearchURL, schoolId) {
    return baseSearchURL + '?includeNearbyLocations=true&school=' + schoolId;
};

export const teacherSearchOnly = function (searchURL) {
    return !!getURLParam(searchURL, 'teachersOnly');
};

export const convertToTeacherSearchURL = function (searchURL, perPage) {
    return buildSchoolOrTeacherURL(searchURL, TEACHER_SEARCH_URL,
        teacherSearchOnly(searchURL), perPage);
};

export const schoolSearchOnly = function (searchURL) {
    return !!getURLParam(searchURL, 'schoolsOnly');
};

export const convertToSchoolSearchURL = function (searchURL, perPage) {
    return buildSchoolOrTeacherURL(searchURL, SCHOOL_SEARCH_URL,
        schoolSearchOnly(searchURL), perPage);
};

/**
 *
 * @param searchURL
 * @param defaultPerPage Note that this is the "perPage" for the parent component and does not necessarily reflect what we're
 * going to use when we search.
 * @returns {?string}
 */
export const convertToEssentialsListSearchURL = function (searchURL, defaultPerPage) {
    // If we've applied filters that can't be applied to essentials lists,
    // we'll just search by urgency
    const urlHasNonEssentialsFilters = hasNonEssentialsFilters(searchURL);

    // Fetch if we're either on page 1 or all filters are applicable to essentials-lists.
    const paramsAllowEssentialsListFetch = !urlHasNonEssentialsFilters || getPage(searchURL) === 1;
    if (!paramsAllowEssentialsListFetch) {
        return null;
    }

    // Written under the assumption that this would be analogous to buildSchoolOrTeacherURL but not analogous enough to
    // share code. It's possible that it's actually _more_ analogous to convertToApiURL, though.

    const perPage = essentialsPerPage(searchURL, defaultPerPage);
    const index = getIndex(searchURL, perPage);

    // For the pilot, we're going to always shuffle search results on the first page
    // https://donorschoose.atlassian.net/browse/WS-33351
    const max = index === 0
        ? ESSENTIALS_CONSTANTS.MAX_WHEN_SHUFFLING
        : perPage;

    const uri = new Uri(ESSENTIALS_LIST_SEARCH_URL).replace('index', index)
        .replace('max', max)
        .replace('APIKey', dcAPIKey);

    // TODO: do we want to make this a block instead of allow approach?
    // That would allow for copying things like cachebusters

    // Transfer all essentials-list-eligible params into this url
    const parsedSearchURL = new Uri(searchURL);
    for (const param of ESSENTIALS_LIST_PARAM_NAMES) {
        const searchUrlValue = parsedSearchURL.getQueryParamValue(param);
        if (searchUrlValue) {
            uri.replace(param, searchUrlValue);
        }
    }

    return uri.toString();
};

export const essentialsPerPage = function (searchURL, defaultPerPage) {
    if (essentialsListSearchOnly(searchURL)) {
        return defaultPerPage;
    } else if (hasNonEssentialsFilters(searchURL)) {
        return ESSENTIALS_CONSTANTS.PROMOS_TO_DISPLAY;
    } else {
        return ESSENTIALS_CONSTANTS.EMBEDDED_TO_DISPLAY;
    }
};

export const essentialsListSearchOnly = function (searchURL) {
    return !!getURLParam(searchURL, 'essentialsListsOnly');
};

function buildSchoolOrTeacherURL(searchURL, baseSearchURL, isOnly, perPage) {
    const params = getURLParams(searchURL);
    if (!params.keywords && !params.map) {
        return null;
    }
    const index = getIndex(searchURL, perPage);
    const max = isOnly ? perPage : 3;

    if (index > 0 && !isOnly) {
        // Only index into results if it's a pure school/teacher search
        return null;
    }
    const uri = new Uri(baseSearchURL).replace('keywords', params.keywords)
        .replace('index', index).replace('max', max);

    if (params.centerLat && params.centerLng) {
        uri.replace('centerLat', params.centerLat).replace('centerLng', params.centerLng);
    }
    return uri.toString();
}

/**
 * Tracks search results with Heap. Sometimes.
 * @param {object[]=} projects The project API results.
 * @param {string=} queryOrigin The place the query originated.
 */
export const trackProjectSearchResults = function (projects, queryOrigin) {
    if (typeof dcAnalytics === 'undefined') {
        return;
    }

    const responseKey = crypto.randomUUID();

    const queryParams = (new Uri(location.href)).query();
    dcAnalytics.track('Search Response - Project', {
        searchTrackingKey: responseKey,
        resultCount: projects.length,
        queryParams: queryParams,
        // Always donorSearchPage for now, but can have other values later to distinguish results shown on the
        // homepage.
        queryOrigin: queryOrigin,
    });

    const now = Date.now();

    if (Math.random() < 0.05) {
        for (let i = 0; i < projects.length; i++) {
            const project = projects[i];
            const daysToExpiration = Math.floor((project.expirationTime - now) / (24 * 60 * 60 * 1000));
            const matchKey = project.matchingFund == null ? '' : project.matchingFund.matchingKey;
            dcAnalytics.track('Search Result - Project', {
                searchTrackingKey: responseKey,
                index: i,
                proposalId: project.id,
                fundingStatus: project.fundingStatus,
                costToComplete: project.costToComplete,
                daysToExpiration: project.fundingStatus === 'needs funding' ? daysToExpiration : -1,
                matchKey: matchKey,
                expandedTo: project.expandedTo,
            });
        }
    }
};

/**
 * Just a little utility-method for implementing our can't-to-this-type-of-search-check functions.
 *
 * @param searchURL
 * @param filterAllowlist Array of query-string-parameters to ignore
 * @return {boolean} true if searchURL contains query-string-parameters that are not tracking params and not found in
 * filterAllowlist
 */
const urlHasOtherFilters = (searchURL, filterAllowlist) => {
    for (const [param] of new Uri(searchURL).queryPairs) {
        // ignore tracking params
        if (TRACKING_PARAMS.includes(param)) {
            continue;
        }

        if (filterAllowlist.includes(param)) {
            continue;
        }

        // We've been hiding teachers & schools whenever there's an unknown param.
        // That's probably not ideal, but it works okay in practice. I'm leaving it alone for now.
        return true;
    }

    return false;
};

export const hasNonEssentialsFilters = (searchURL) => {
    // Allow location and EFS search for essentials but not teachers/schools
    return urlHasOtherFilters(searchURL, ESSENTIALS_LIST_PARAM_NAMES);
};
export const hasNonTeacherSchoolFilters = (searchURL) => {
    // Allow keyword search for teachers & schools but not essentials
    return urlHasOtherFilters(searchURL, ['keywords']);
};
export const hasNonLocationFilters = (searchURL) => {
    return urlHasOtherFilters(searchURL, locationParamNames);
}
